import React, { useState, useEffect } from 'react';
import { getAlerts, markAlertRead } from '../utils/api';
import { toast } from 'sonner';
import { AlertTriangle, Info, CheckCircle2, Bell, ChevronDown, ChevronUp, Activity } from 'lucide-react';

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedMarkers, setExpandedMarkers] = useState(new Set());

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const data = await getAlerts();
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markAlertRead(id);
      setAlerts(alerts.map(a => a.id === id ? { ...a, read: true } : a));
    } catch (error) {
      toast.error('Failed to mark alert as read');
    }
  };

  const toggleMarkerExpansion = (marker) => {
    const newExpanded = new Set(expandedMarkers);
    if (newExpanded.has(marker)) {
      newExpanded.delete(marker);
    } else {
      newExpanded.add(marker);
    }
    setExpandedMarkers(newExpanded);
  };

  // Extract marker from alert message
  const getMarkerFromMessage = (message) => {
    const markers = ['WBC', 'LYMPHOCYTES', 'PLATELETS', 'HAEMOGLOBIN', 'NEUTROPHILS'];
    for (const marker of markers) {
      if (message.includes(marker)) {
        return marker;
      }
    }
    return 'OTHER';
  };

  // Group alerts by marker
  const groupAlertsByMarker = (alertsList) => {
    const grouped = {};
    alertsList.forEach(alert => {
      const marker = getMarkerFromMessage(alert.message);
      if (!grouped[marker]) {
        grouped[marker] = [];
      }
      grouped[marker].push(alert);
    });
    
    // Sort alerts within each group by date (newest first)
    Object.keys(grouped).forEach(marker => {
      grouped[marker].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    
    return grouped;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'unread') return !alert.read;
    if (filter === 'read') return alert.read;
    return true;
  });

  const groupedAlerts = groupAlertsByMarker(filteredAlerts);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-amber-600" />;
      case 'info':
        return <Info className="h-6 w-6 text-blue-600" />;
      default:
        return <Bell className="h-6 w-6 text-slate-600" />;
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const getMarkerDisplayName = (marker) => {
    const names = {
      'WBC': 'White Blood Cells',
      'LYMPHOCYTES': 'Lymphocytes',
      'PLATELETS': 'Platelets',
      'HAEMOGLOBIN': 'Haemoglobin',
      'NEUTROPHILS': 'Neutrophils',
      'OTHER': 'Other'
    };
    return names[marker] || marker;
  };

  const getReassuranceText = (alert) => {
    if (alert.type === 'clinical_range') {
      return 'Single readings can fluctuate and trends over time are more important.';
    } else if (alert.type === 'baseline_change') {
      return 'This is an informational notice. Discuss any concerns with your healthcare provider.';
    }
    return 'This alert is for informational purposes only.';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="alerts-page" className="space-y-6 md:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-2">Health Alerts</h1>
        <p className="text-base md:text-lg leading-relaxed text-slate-700">Notifications about significant changes in your blood work patterns</p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl md:rounded-2xl p-4 md:p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800 mb-1 md:mb-2">Important Reminder</p>
            <p className="text-xs md:text-sm text-red-700 leading-relaxed">
              These alerts are informational only and are not medical diagnoses. 
              Significant changes in your blood work or symptoms should always be discussed with your healthcare provider.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Buttons - Stack on mobile, row on larger screens */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          data-testid="filter-all-button"
          className={`px-3 md:px-4 py-2 rounded-full text-sm md:text-base font-medium transition-all ${
            filter === 'all'
              ? 'bg-primary text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All ({alerts.length})
        </button>
        <button
          onClick={() => setFilter('unread')}
          data-testid="filter-unread-button"
          className={`px-3 md:px-4 py-2 rounded-full text-sm md:text-base font-medium transition-all ${
            filter === 'unread'
              ? 'bg-primary text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Unread ({alerts.filter(a => !a.read).length})
        </button>
        <button
          onClick={() => setFilter('read')}
          data-testid="filter-read-button"
          className={`px-3 md:px-4 py-2 rounded-full text-sm md:text-base font-medium transition-all ${
            filter === 'read'
              ? 'bg-primary text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Read ({alerts.filter(a => a.read).length})
        </button>
      </div>

      {/* Alerts List - Grouped by Marker */}
      <div className="space-y-4 md:space-y-6">
        {Object.keys(groupedAlerts).length === 0 ? (
          <div className="bg-white rounded-xl md:rounded-2xl p-8 md:p-12 border border-slate-100 shadow-sm text-center">
            <Bell className="h-12 w-12 md:h-16 md:w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-2">No alerts</h3>
            <p className="text-sm md:text-base text-slate-600">
              {filter === 'all' && 'You don\'t have any alerts yet'}
              {filter === 'unread' && 'You don\'t have any unread alerts'}
              {filter === 'read' && 'You don\'t have any read alerts'}
            </p>
          </div>
        ) : (
          Object.entries(groupedAlerts).map(([marker, markerAlerts]) => {
            const isExpanded = expandedMarkers.has(marker);
            const mostRecentAlert = markerAlerts[0];
            const olderAlerts = markerAlerts.slice(1);
            const hasOlderAlerts = olderAlerts.length > 0;
            
            return (
              <div key={marker} className="bg-white rounded-xl md:rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {/* Marker Header */}
                <div className="bg-slate-50 border-b border-slate-100 px-4 md:px-6 py-3 md:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2 md:gap-3">
                      <Activity className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                      <h3 className="text-base md:text-lg font-semibold text-slate-900">{getMarkerDisplayName(marker)}</h3>
                      <span className="px-2 py-0.5 md:py-1 bg-slate-200 text-slate-700 text-xs font-medium rounded-full">
                        {markerAlerts.length} alert{markerAlerts.length > 1 ? 's' : ''}
                      </span>
                      {markerAlerts.some(a => !a.read) && (
                        <span className="px-2 py-0.5 md:py-1 bg-primary text-white text-xs font-medium rounded-full">
                          {markerAlerts.filter(a => !a.read).length} new
                        </span>
                      )}
                    </div>
                    {hasOlderAlerts && (
                      <button
                        onClick={() => toggleMarkerExpansion(marker)}
                        data-testid={`expand-${marker.toLowerCase()}`}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs md:text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors self-start sm:self-auto"
                      >
                        <span>{isExpanded ? 'Hide' : 'Show'} older</span>
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </div>

                {/* Most Recent Alert */}
                <div className="p-4 md:p-6">
                  <div
                    className={`rounded-lg md:rounded-xl p-4 md:p-5 border ${getSeverityBg(mostRecentAlert.severity)} ${
                      !mostRecentAlert.read ? 'shadow-sm' : 'opacity-75'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="mt-0.5 flex-shrink-0">{getSeverityIcon(mostRecentAlert.severity)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                              {mostRecentAlert.type.replace('_', ' ')}
                            </span>
                            {!mostRecentAlert.read && (
                              <span className="px-2 py-0.5 bg-primary text-white text-xs font-medium rounded-full">
                                New
                              </span>
                            )}
                            <span className="text-xs text-slate-500">Most Recent</span>
                          </div>
                          <p className="text-sm md:text-base text-slate-900 leading-relaxed mb-3">{mostRecentAlert.message}</p>
                          
                          {/* Reassurance Text */}
                          <div className="bg-white/60 border border-slate-200 rounded-lg p-2.5 md:p-3 mb-3">
                            <p className="text-xs md:text-sm text-slate-600 italic">
                              💡 {getReassuranceText(mostRecentAlert)}
                            </p>
                          </div>
                          
                          <p className="text-xs text-slate-500">
                            {new Date(mostRecentAlert.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      {!mostRecentAlert.read && (
                        <button
                          onClick={() => handleMarkRead(mostRecentAlert.id)}
                          data-testid={`mark-read-${mostRecentAlert.id}`}
                          className="p-2 text-slate-400 hover:text-primary hover:bg-white rounded-lg transition-colors flex-shrink-0 self-start"
                          title="Mark as read"
                        >
                          <CheckCircle2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Older Alerts (Collapsible) */}
                  {isExpanded && hasOlderAlerts && (
                    <div className="mt-4 space-y-3 pt-4 border-t border-slate-100">
                      <p className="text-xs md:text-sm font-medium text-slate-600 mb-3">Historical Alerts</p>
                      {olderAlerts.map((alert) => (
                        <div
                          key={alert.id}
                          className={`rounded-lg md:rounded-xl p-3 md:p-4 border ${getSeverityBg(alert.severity)} ${
                            !alert.read ? 'shadow-sm' : 'opacity-60'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                            <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                              <div className="mt-0.5 flex-shrink-0">{getSeverityIcon(alert.severity)}</div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1.5 md:mb-2">
                                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                                    {alert.type.replace('_', ' ')}
                                  </span>
                                  {!alert.read && (
                                    <span className="px-2 py-0.5 bg-primary text-white text-xs font-medium rounded-full">
                                      New
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs md:text-sm text-slate-800 leading-relaxed mb-2">{alert.message}</p>
                                <p className="text-xs text-slate-500">
                                  {new Date(alert.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                            {!alert.read && (
                              <button
                                onClick={() => handleMarkRead(alert.id)}
                                data-testid={`mark-read-${alert.id}`}
                                className="p-2 text-slate-400 hover:text-primary hover:bg-white rounded-lg transition-colors flex-shrink-0 self-start"
                                title="Mark as read"
                              >
                                <CheckCircle2 className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AlertsPage;